import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { COMPANY_INFO } from '../data/company';
import { fetchCmsServices } from '../lib/cms';
import ContactFormSection from '../components/ContactFormSection';

export default function ServicesOverviewPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      const svcs = await fetchCmsServices();
      setServices(svcs);
      setLoading(false);
    }
    loadData();
  }, []);

  return (
    <>
      <Helmet>
        <title>Rengøringsydelser - OP Rengøringsservice</title>
        <meta name="description" content="Oversigt over rengøringsydelser fra OP Rengøringsservice: Privatrengøring, erhvervsrengøring, snerydning, haveservice, vinduespolering, måtteservice og tagrenderensning." />
        <meta property="og:title" content="Rengøringsydelser - OP Rengøringsservice" />
        <meta property="og:description" content="Oversigt over rengøringsydelser i Nibe. Kontakt OP Rengøringsservice for et uforpligtende tilbud." />
        <link rel="canonical" href="https://opservice.dk/ydelser" />
      </Helmet>

      {/* Header Banner */}
      <section className="bg-slate-900 text-white py-16 lg:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center max-w-3xl">
          <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20 inline-block mb-3">
            Vores Serviceydelser
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4">
            Rengøringsydelser til private og erhverv
          </h1>
          <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
            OP Rengøringsservice tilbyder fleksible og professionelle løsninger skræddersyet til dine ønsker og behov.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="py-20 text-center text-slate-600 font-bold flex items-center justify-center gap-3">
              <i className="fa-solid fa-spinner animate-spin text-xl text-sky-800" aria-hidden="true" />
              <span>Henter serviceydelser...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((svc) => (
                <Link
                  key={svc.slug.da || svc.slug}
                  to={`/ydelser/${svc.slug.da || svc.slug}`}
                  className="bg-white rounded-3xl overflow-hidden border border-slate-200/80 shadow-xs hover:shadow-xl transition-all duration-300 flex flex-col justify-between group"
                >
                  <div className="relative aspect-[16/10] bg-slate-100 overflow-hidden">
                    <img
                      src={svc.primary_image?.[0] || "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/rengoering-erhverv-gulvrengoering-i-lang-gang-01.jpg"}
                      alt={svc.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm p-3 rounded-2xl shadow-md text-sky-800 text-lg">
                      <i className={svc.icon || 'fa-solid fa-sparkles'} aria-hidden="true" />
                    </div>
                  </div>

                  <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between">
                    <div>
                      <h2 className="text-xl font-extrabold text-slate-900 group-hover:text-sky-800 transition-colors mb-3">
                        {svc.title}
                      </h2>
                      <p className="text-slate-600 text-sm leading-relaxed line-clamp-3">
                        {svc.short_description}
                      </p>
                    </div>

                    <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-sky-800">
                      <span>Se detaljer & priser</span>
                      <i className="fa-solid fa-arrow-right group-hover:translate-x-1.5 transition-transform" aria-hidden="true" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Direct Contact Banner */}
      <section className="py-12 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <h3 className="text-2xl font-bold">Har du en særlig eller sammensat opgave?</h3>
            <p className="text-emerald-100 text-sm mt-1">
              Vi skræddersyr gerne en samlet aftale til både erhverv og private boliger.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="bg-white text-emerald-900 hover:bg-emerald-50 font-bold px-5 py-3 rounded-xl transition-colors text-sm shadow whitespace-nowrap-custom"
            >
              <i className="fa-solid fa-phone mr-2 text-emerald-600" aria-hidden="true" />
              <span>Ring: {COMPANY_INFO.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <ContactFormSection />
    </>
  );
}
